import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  Mic, 
  MessageSquare, 
  Settings, 
  Clock, 
  Plus,
  BookOpen,
  ChevronRight,
  LogOut,
  AlertCircle,
  GraduationCap,
  Brain,
  Folder,
  FolderOpen,
  FileText,
  CheckCircle2,
  Hourglass,
  MoreHorizontal,
  Headphones,
  Sparkles,
  Calendar,
  BarChart3,
  Target,
  TrendingUp,
  Zap
} from 'lucide-react';
import { formatDistanceToNow, format, differenceInDays, isPast } from 'date-fns';
import { isLlmInferenceAvailable } from '../lib/aiCapability';

// Format duration compactly
const formatDuration = (seconds) => {
  if (!seconds) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

// Compact status indicator
const StatusDot = ({ status }) => {
  const colors = {
    completed: 'bg-green-500',
    processing: 'bg-amber-500',
    failed: 'bg-red-500',
  };
  return (
    <span 
      className={`w-2 h-2 rounded-full ${colors[status] || colors.processing}`}
      title={status}
    />
  );
};

// Compact lecture row for dashboard
const CompactLectureItem = ({ lecture }) => {
  const isNew = lecture.created_at && 
    new Date(lecture.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000);
  
  return (
    <Link
      to={`/lecture/${lecture.id}`}
      className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors group text-sm"
      data-testid={`lecture-item-${lecture.id}`}
    >
      <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
      
      <span className="flex-1 truncate text-foreground">
        {lecture.title}
      </span>

      {isNew && (
        <span className="px-1 py-0.5 rounded text-[9px] font-medium bg-blue-500/10 text-blue-600">
          New
        </span>
      )}

      <span className="text-[11px] text-muted-foreground whitespace-nowrap">
        {formatDistanceToNow(new Date(lecture.created_at), { addSuffix: true })}
      </span>

      {lecture.audio_duration && (
        <span className="text-[11px] text-muted-foreground whitespace-nowrap w-12 text-right">
          {formatDuration(lecture.audio_duration)}
        </span>
      )}

      <StatusDot status={lecture.status} />
      
      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </Link>
  );
};

// Compact subject folder for dashboard
const CompactSubjectFolder = ({ subject, lectures, maxVisible = 2 }) => {
  const [isOpen, setIsOpen] = useState(true);
  const subjectLectures = lectures
    .filter(l => subject ? l.subject_id === subject.id : !l.subject_id)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  
  const visibleLectures = subjectLectures.slice(0, maxVisible);
  const hiddenCount = subjectLectures.length - maxVisible;
  const folderColor = subject?.color || '#6b7280';
  
  if (subjectLectures.length === 0) return null;
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50 transition-colors w-full text-left text-sm">
        <ChevronRight 
          className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} 
        />
        {isOpen ? (
          <FolderOpen className="w-4 h-4" style={{ color: folderColor }} />
        ) : (
          <Folder className="w-4 h-4" style={{ color: folderColor }} />
        )}
        <span className="font-medium text-foreground flex-1 truncate">
          {subject?.name || 'Uncategorized'}
        </span>
        {subject?.course_code && (
          <span className="text-[10px] text-muted-foreground">({subject.course_code})</span>
        )}
        <span className="text-[11px] text-muted-foreground">
          {subjectLectures.length}
        </span>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="ml-5">
        {visibleLectures.map(lecture => (
          <CompactLectureItem key={lecture.id} lecture={lecture} />
        ))}
        {hiddenCount > 0 && (
          <Link 
            to="/subjects"
            className="flex items-center gap-2 py-1 px-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            <MoreHorizontal className="w-3 h-3" />
            {hiddenCount} more lecture{hiddenCount !== 1 ? 's' : ''}
          </Link>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [lectures, setLectures] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [usage, setUsage] = useState(null);
  const [studyDashboard, setStudyDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [lecturesData, subjectsData, usageData, studyData] = await Promise.all([
        api.getLectures(),
        api.getSubjects(),
        api.getUsage(),
        api.getStudyDashboard().catch(() => null) // Don't fail if no exams yet
      ]);
      setLectures(lecturesData);
      setSubjects(subjectsData);
      setUsage(usageData);
      setStudyDashboard(studyData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const hasApiKey = isLlmInferenceAvailable(user);

  // Sort subjects by most recent lecture activity
  const sortedSubjects = [...subjects].sort((a, b) => {
    const aLatest = lectures.find(l => l.subject_id === a.id);
    const bLatest = lectures.find(l => l.subject_id === b.id);
    if (!aLatest) return 1;
    if (!bLatest) return -1;
    return new Date(bLatest.created_at) - new Date(aLatest.created_at);
  });

  // Check if there are uncategorized lectures
  const hasUncategorized = lectures.some(l => !l.subject_id);

  // Total lecture count
  const totalLectures = lectures.length;

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/dashboard" className="font-serif text-xl text-foreground">
            LectureAI
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/progress">
              <Button variant="ghost" size="sm" className="gap-2 hidden sm:inline-flex">
                <BarChart3 className="w-4 h-4" />
                Progress
              </Button>
            </Link>
            <Link to="/settings">
              <Button variant="ghost" size="icon" data-testid="settings-btn">
                <Settings className="w-5 h-5" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl md:text-4xl text-foreground mb-2">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-muted-foreground">
            Ready to enhance your learning experience?
          </p>
        </div>

        {/* API Key Warning */}
        {!hasApiKey && (
          <Card className="mb-8 border-amber-500/50 bg-amber-500/5" data-testid="api-key-warning">
            <CardContent className="flex flex-col items-start gap-3 py-4 sm:flex-row sm:items-center sm:gap-4">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-foreground">AI Features Need Configuration</p>
                <p className="text-sm text-muted-foreground">
                  Add your API key in settings to use transcription and Q&A features.
                </p>
              </div>
              <Link to="/settings">
                <Button variant="outline" size="sm" className="w-full sm:w-auto" data-testid="add-api-key-btn">
                  Add Key
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Bento Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Quick Actions - Takes 1 column */}
          <div className="space-y-6">
            {/* New Lecture Card */}
            <Card className="border-border/50 shadow-card card-hover overflow-hidden" data-testid="new-lecture-card">
              <CardContent className="p-0">
                <Link to="/lecture" className="block p-6">
                  <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mb-4">
                    <Mic className="w-7 h-7 text-primary-foreground" />
                  </div>
                  <h3 className="font-serif text-xl mb-2">Record Lecture</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Start a new recording and get AI-formatted notes
                  </p>
                  <div className="flex items-center text-primary text-sm font-medium">
                    Start Recording
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </Link>
              </CardContent>
            </Card>

            {/* Q&A Mode Card */}
            <Card className="border-border/50 shadow-card card-hover overflow-hidden" data-testid="qa-mode-card">
              <CardContent className="p-0">
                <Link to="/qa" className="block p-6">
                  <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-4">
                    <MessageSquare className="w-7 h-7 text-secondary-foreground" />
                  </div>
                  <h3 className="font-serif text-xl mb-2">Q&A Mode</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Ask questions about your lectures using voice or text
                  </p>
                  <div className="flex items-center text-secondary-foreground text-sm font-medium">
                    Start Q&A
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </Link>
              </CardContent>
            </Card>

            {/* Quick Review Card */}
            <Card className="border-border/50 shadow-card card-hover overflow-hidden" data-testid="quick-review-card">
              <CardContent className="p-0">
                <Link to="/review" className="block p-6">
                  <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-4">
                    <Brain className="w-7 h-7 text-amber-500" />
                  </div>
                  <h3 className="font-serif text-xl mb-2">Quick Review</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    AI-curated exam questions from your lectures
                  </p>
                  <div className="flex items-center text-amber-500 text-sm font-medium">
                    Start Review
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </Link>
              </CardContent>
            </Card>

            {/* QnA Review + Card */}
            <Card className="border-border/50 shadow-card card-hover overflow-hidden relative" data-testid="qna-review-card">
              <div className="absolute top-3 right-3">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary">
                  <Sparkles className="w-3 h-3" />
                  Voice
                </span>
              </div>
              <CardContent className="p-0">
                <Link to="/qna-review" className="block p-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center mb-4">
                    <Headphones className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl mb-2">QnA Review +</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Voice-powered exam prep that follows your closest exam and subject mastery
                  </p>
                  <div className="flex items-center text-primary text-sm font-medium">
                    Start Voice Review
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </Link>
              </CardContent>
            </Card>

            {/* Exam Schedule Card */}
            <Card className="border-border/50 shadow-card card-hover overflow-hidden" data-testid="exam-schedule-card">
              <CardContent className="p-0">
                <Link to="/exams" className="block p-6">
                  <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mb-4">
                    <Calendar className="w-7 h-7 text-red-500" />
                  </div>
                  <h3 className="font-serif text-xl mb-2">Exam Schedule</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Track deadlines, record scores, and launch the right review mode instantly
                  </p>
                  <div className="flex items-center text-red-500 text-sm font-medium">
                    View Schedule
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </Link>
              </CardContent>
            </Card>

            {/* Study Recommendation Card - Only show if there's a recommendation */}
            {studyDashboard?.recommendation && (
              <Card className={`border-border/50 shadow-card overflow-hidden ${
                studyDashboard.recommendation.urgency === 'critical' ? 'border-red-500/50 bg-red-500/5' :
                studyDashboard.recommendation.urgency === 'high' ? 'border-orange-500/50 bg-orange-500/5' :
                ''
              }`} data-testid="study-recommendation-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <Zap className={`w-4 h-4 ${
                      studyDashboard.recommendation.urgency === 'critical' ? 'text-red-500' :
                      studyDashboard.recommendation.urgency === 'high' ? 'text-orange-500' :
                      'text-primary'
                    }`} />
                    <CardTitle className="font-serif text-lg">Study Now</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 mb-2">
                    {studyDashboard.recommendation.color && (
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: studyDashboard.recommendation.color }}
                      />
                    )}
                    <span className="font-medium">{studyDashboard.recommendation.subject_name}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {studyDashboard.recommendation.recommendation}
                  </p>
                  <Link to="/review">
                    <Button size="sm" className="w-full rounded-xl">
                      <Target className="w-4 h-4 mr-2" />
                      Start Review
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Exams Card - Only show if there are upcoming exams */}
            {studyDashboard?.upcoming_exams?.length > 0 && (
              <Card className="border-border/50 shadow-card" data-testid="upcoming-exams-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-serif text-lg">Upcoming Exams</CardTitle>
                    <Link to="/exams">
                      <Button variant="ghost" size="sm" className="h-7 text-xs">
                        View All
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {studyDashboard.upcoming_exams.slice(0, 3).map((exam) => (
                    <div 
                      key={exam.id}
                      className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {exam.subject_color && (
                          <div 
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: exam.subject_color }}
                          />
                        )}
                        <span className="text-sm truncate">{exam.title}</span>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                        exam.days_until === 0 ? 'bg-red-500 text-white' :
                        exam.days_until <= 3 ? 'bg-orange-500 text-white' :
                        exam.days_until <= 7 ? 'bg-amber-500 text-white' :
                        'bg-primary/10 text-primary'
                      }`}>
                        {exam.days_until === 0 ? 'Today' :
                         exam.days_until === 1 ? 'Tomorrow' :
                         `${exam.days_until}d`}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Exams Needing Scores Alert */}
            {studyDashboard?.exams_needing_scores?.length > 0 && (
              <Card className="border-amber-500/50 bg-amber-500/5" data-testid="exams-needing-scores-card">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">
                        {studyDashboard.exams_needing_scores.length} exam{studyDashboard.exams_needing_scores.length !== 1 ? 's' : ''} need scores
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Recording scores improves recommendations
                      </p>
                    </div>
                    <Link to="/exams?tab=past">
                      <Button variant="outline" size="sm" className="h-7 text-xs">
                        Add
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Usage Stats Card */}
            <Card className="border-border/50 shadow-card" data-testid="usage-stats-card">
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-lg">Usage Stats</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Spent</span>
                      <span className="font-medium">${usage?.total_cost?.toFixed(4) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Audio Transcribed</span>
                      <span className="font-medium">
                        {Math.floor((usage?.total_audio_seconds || 0) / 60)}m
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tokens Used</span>
                      <span className="font-medium">
                        {((usage?.total_tokens_input || 0) + (usage?.total_tokens_output || 0)).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Recent Lectures - Takes 2 columns - Finder Style */}
          <div className="md:col-span-2">
            <Card className="border-border/50 shadow-card h-full" data-testid="recent-lectures-card">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                  <CardTitle className="font-serif text-xl">My Lectures</CardTitle>
                  <CardDescription>
                    {totalLectures} lecture{totalLectures !== 1 ? 's' : ''} in {subjects.length} subject{subjects.length !== 1 ? 's' : ''}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Link to="/subjects">
                    <Button variant="outline" size="sm" className="rounded-xl" data-testid="view-all-btn">
                      <Folder className="w-4 h-4 mr-2" />
                      View All
                    </Button>
                  </Link>
                  <Link to="/lecture">
                    <Button size="sm" className="rounded-xl" data-testid="new-lecture-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 p-2">
                        <Skeleton className="w-4 h-4 rounded" />
                        <Skeleton className="h-4 flex-1" />
                        <Skeleton className="h-4 w-16" />
                      </div>
                    ))}
                  </div>
                ) : lectures.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="font-medium text-foreground mb-2">No lectures yet</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Record your first lecture to get started
                    </p>
                    <Link to="/lecture">
                      <Button className="rounded-xl" data-testid="first-lecture-btn">
                        <Mic className="w-4 h-4 mr-2" />
                        Record First Lecture
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Subject folders sorted by activity */}
                    {sortedSubjects.map((subject) => (
                      <CompactSubjectFolder 
                        key={subject.id} 
                        subject={subject} 
                        lectures={lectures}
                        maxVisible={2}
                      />
                    ))}
                    
                    {/* Uncategorized at the end */}
                    {hasUncategorized && (
                      <CompactSubjectFolder 
                        subject={null} 
                        lectures={lectures}
                        maxVisible={2}
                      />
                    )}

                    {/* Manage subjects link */}
                    <div className="pt-3 border-t mt-3">
                      <Link 
                        to="/subjects"
                        className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <GraduationCap className="w-4 h-4" />
                        Manage Subjects & All Lectures
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
